import ApiService from '../../services/api.service';
import { entityTypes, lpsFormTypes } from '../../constants/field-constants';
import {
  USER_RE,
  SLUG_RE,
  BAD_VALUE_RE,
  BAD_CITY_RE,
  HOUSE_NUMBER_RE,
  INDEX_ADDRESS_RE
} from '../../constants/patterns-constants';
import AlertService from '../../services/notification.service';
import type { UserState } from '../modal/containers/user-modal/UserModal';

export const ALLOWED_PLATFORMS = ["twitter", "linkedin", "facebook"] as const;
export type SocialPlatform = typeof ALLOWED_PLATFORMS[number];
export const isSocialPlatform = (v: unknown): v is SocialPlatform => {
  return (
    typeof v === 'string' &&
    (ALLOWED_PLATFORMS as readonly string[]).includes(v.toString())
  )
}
export const toPlatform = (v: unknown): SocialPlatform | undefined => {
  if (typeof v !== 'string') return undefined;
  const s = v.trim().toLowerCase();
  if (s === "" || s === "generic" || s === "none") return undefined;
  if (s === "x") return "twitter";
  return isSocialPlatform(s) ? (s as SocialPlatform) : undefined;
}
const hostIsOneOf = (u: URL, list: string[]) => list.includes(u.hostname.toLowerCase());
const segs = (u: URL) => u.pathname.split('/').filter(Boolean);

export const validatePlatform = (platform: SocialPlatform, url: string) => {
  try {
    const u = new URL(url);
    switch (platform) {
      case "twitter":
        if (!hostIsOneOf(u, ["twitter.com", "www.twitter.com", "x.com", "www.x.com"])) {
          return {
            ok: false,
            reason: "The link must lead to twitter.com or x.com"
          }
        }
        if (segs(u).length !== 1) return {
          ok: false,
          reason: "Please enter your profile name: https://twitter.com/username"
        }
        const name = segs(u)[0].replace(/^@/g, '');
        return {
          ok: USER_RE.test(name),
          reason: "Incorrect username"
        }
      case 'linkedin':
        if (!hostIsOneOf(u, ["linkedin.com", "www.linkedin.com"])) {
          return {
            ok: false,
            reason: "The link must lead to linkedin.com"
          }
        }
        const p = segs(u);
        const ok = (p.length === 2 && p[0] === "in" && SLUG_RE.test(p[1]))
          || (p.length === 2 && p[0] === "company" && SLUG_RE.test(p[1]))
          || (p.length === 2 && p[0] === "groups" && SLUG_RE.test(p[1]));
        return {
          ok,
          reason: "Example: https://www.linkedin.com/in/username"
        };
      case 'facebook':
        if (!hostIsOneOf(u, ["facebook.com", "www.facebook.com", "m.facebook.com"])) {
          return {
            ok: false,
            reason: "The link must lead to facebook.com"
          }
        }
        const s = segs(u);
        if (u.pathname === "/profile.php" && u.searchParams.has("id")) {
          return {
            ok: /^\d+$/.test(u.searchParams.get("id") || ""),
            reason: "Invalid profile id"
          }
        }
        if (s.length >= 2 && s[0] === "pages") {
          const id = s[s.length - 1];
          return {
            ok: /^\d+$/.test(id),
            reason: "Invalid Facebook page format"
          }
        }
        if (s.length === 1) {
          const slug = s[0].toLowerCase();
          if (u.toString().includes("profile.php")) {
            return {
              ok: false,
              reason: "Link profile.php without id is not valid"
            }
          }
          return {
          ok: SLUG_RE.test(slug),
          reason: "Invalid page/profile username"
        }
        }
        if (s.length === 2 && s[0] === "groups") return {
          ok: SLUG_RE.test(s[1]),
          reason: "Invalid group slug"
        }

        return {
          ok: false,
          reason: "Please provide a link to your profile/page/groups"
        }
    }
  } catch {
    return {
      ok: false,
      reason: "Invalid URL"
    };
  }
}

function hasDuplicates(variablesArray: any) {
  return new Set(variablesArray).size !== variablesArray.length;
}

export const emailValidator = (email: string) => {
  // let isValid = false;
  // const googleRegex  = /^[a-zA-Z0-9._%+-]+@(gmail\.com$|randvast\.nl$|stedacloudimpact\.nl)$/;
  // if (email !== "")
  //   isValid = googleRegex.test(email);
  // return isValid;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
  if (!email) return false;
  return emailRegex.test(email);
};

export const userFormValidator = (user: UserState) => {
  let validationResult: {
    isFilledName: boolean;
    isFilledSurname: boolean;
    isFilledEmail: boolean;
    isFilledRole: boolean;
    isFilledDefaultLpsProfile: boolean;
    isFilledIP: boolean;
    isValidEmail: boolean;
  } = {
    isFilledName: true,
    isFilledSurname: true,
    isFilledEmail: true,
    isFilledRole: true,
    isFilledDefaultLpsProfile: true,
    isFilledIP: true,
    isValidEmail: true,
  };
  if (!user.name) validationResult.isFilledName = false;
  if (!user.surname) validationResult.isFilledSurname = false;
  if (!user.email) {
    validationResult.isFilledEmail = false;
    validationResult.isValidEmail = false;
  } else {
    const isValidEmail = emailValidator(user.email);

    if (!isValidEmail) validationResult.isValidEmail = false;
  }
  if (!user.role_id) validationResult.isFilledRole = false;
  if (user.ip_restriction && user.user_ip_whitelist?.length === 0)
    validationResult.isFilledIP = false;
  if (
    user.user_ip_whitelist?.length &&
    (user.user_ip_whitelist ?? []).some((item ) => !item.ip)
  )
    validationResult.isFilledIP = false;

  return validationResult;
};

export const kadIdValidator = async (type: string, kadId: any) => {
  // TODO The check for a match between the cadastre number and the database fails

  // if (type === entityTypes.PERSON || type === entityTypes.COMPANY) {
  //   return await ApiService.post(`/${type}/kad_id`, kadId);
  // }
  if (type === 'entity' || type === 'object') {
    return await ApiService.post(`/${type}/kad_id`, { kad_id: kadId });
  }
};

export const companyNameValidator = async (type: string, entityCompany: string) => {
  if (type === "entity") {
    return await ApiService.post(`/${type}/company_name`, { company_name: entityCompany });
  }
};

export const entityFormValidator = (entity: any) => {
  let validationResult: {
    isFilledLastName: boolean;
    isFilledCompanyName: boolean;
  } = {
    isFilledLastName: true,
    isFilledCompanyName: true,
  };

  if (
    entity.general_fields.entity_type === entityTypes.PERSON &&
    !entity.general_fields.last_name
  ) {
    validationResult.isFilledLastName = false;
  }
  if (
    entity.general_fields.entity_type === entityTypes.COMPANY &&
    !entity.general_fields.company_name
  ) {
    validationResult.isFilledCompanyName = false;
  }

  return validationResult;
};

export const objectFormValidator = (object: any) => {
  let validationResult: {
    isFilledKadId: boolean;
    isFilledCityCode: boolean;
    isFilledPlotNumber: boolean;
    isFilledSection: boolean;
    isFilledPlotIndex: boolean;
    isFilledCity: boolean;
  } = {
    isFilledKadId: true,
    isFilledCityCode: true,
    isFilledPlotNumber: true,
    isFilledSection: true,
    isFilledPlotIndex: true,
    isFilledCity: true,
  };

  if (!object.general_fields.kadaster_id)
    validationResult.isFilledKadId = false;
  if (!object.plot.city_code) validationResult.isFilledCityCode = false;
  if (!object.plot.section) validationResult.isFilledSection  = false;
  if (!object.plot.plot_number) validationResult.isFilledPlotNumber = false;
  if (!object.plot.index) validationResult.isFilledPlotIndex = false;
  if (!object.address.city_name) validationResult.isFilledCity = false;

  return validationResult;
};

export interface Variable {
  id: number;
  value: boolean;
}

interface  ObjectConditionRules {
  variable?: string,
  condition?: string,
  condition_value?: string,
  condition_points?: string,
}

const  checkObject = (obj: ObjectConditionRules, keysToCheck: string[]): boolean => {
  if (!obj || Object.keys(obj).length === 0) {
      return false;
  }
  for (let key of keysToCheck) {
    // if (!obj.hasOwnProperty(key) || obj[key] === undefined || obj[key] === null) {
    if (!obj.hasOwnProperty(key) || !obj[key] || obj[key] === undefined || obj[key] === null ) {
        return false;
    }
  }
  return true;
}

export const validateLPSConfig = (data: any, lpsConfigs: any) => {
  const warning = () => {
    AlertService.warning("Please fill in all required fields");
  };
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    lps_config_cities,
    general_fields,
    lpsc_object_condition_rules,
    lps_config_entity_KOA_rules,
    id,
  } = data;

  let isFilledFieldVariables: Variable[] = [];
  let result: {
    isFilledFieldCalculationType: boolean;
    isFilledFieldName: boolean;
    isFilledFieldCity: boolean;
    isFilledFieldObjectConditionRulesVariable: boolean;
    isFilledFieldObjectConditionRules: boolean,
    isFilledFieldsObjectUnitsAmount: boolean,
    isFilledFieldConfigEntityKoaPoints: boolean;
    isNotDuplicatedFields: boolean;
    isNotDuplicatedFieldName: boolean;
    isFilledFieldConfigType: boolean;
    isFilledFieldObjectType: boolean;
    isFilledFieldEntityType: boolean;
  } = {
    isFilledFieldCalculationType: true,
    isFilledFieldName: true,
    isFilledFieldCity: true,
    isFilledFieldObjectConditionRulesVariable: true,
    isFilledFieldObjectConditionRules: true,
    isFilledFieldsObjectUnitsAmount: true,
    isFilledFieldConfigEntityKoaPoints: true,
    isNotDuplicatedFields: true,
    isNotDuplicatedFieldName: true,
    isFilledFieldConfigType: true,
    isFilledFieldObjectType: true,
    isFilledFieldEntityType: true,
  };

  result.isFilledFieldCalculationType = !!general_fields.calculation_type;

  if (!general_fields.name) {
    result.isFilledFieldName = false;
  }

  if (general_fields.name) {
    const nameExists = lpsConfigs.some((item: any) => item.name === general_fields.name && item.id !== id );
    if (nameExists) {
      AlertService.warning(
        "LPS config with this name already exists, please change it",
      );
      result.isNotDuplicatedFieldName = false;
    } else {
      result.isNotDuplicatedFieldName = true;
    }
  }

  if (!general_fields.config_type) {
    result.isFilledFieldConfigType = false;
  }
  // if (!general_fields.object_type) {
  //   result.isFilledFieldObjectType = false;
  // }
  // if (!general_fields.entity_type) {
  //   result.isFilledFieldEntityType = false;
  // }

  if (
    general_fields.calculation_type === lpsFormTypes.NON_INDUSTRIAL ||
    general_fields.calculation_type === lpsFormTypes.INDUSTRIAL
  ) {

    lpsc_object_condition_rules.map((rule: any, index: number ) => {
      if (rule.variable !== "units amount") {
        result.isFilledFieldObjectConditionRules = checkObject(rule, ["variable", "condition", "condition_value", "condition_points"]);
      } else {
        result.isFilledFieldsObjectUnitsAmount = checkObject(rule, ["variable", "condition", "condition_value", "condition_points"]);
      }

      if (rule.variable === '' || rule.variable === undefined) {
        isFilledFieldVariables = [...isFilledFieldVariables, ...[{id: index, value: false}]]
      } else {
        isFilledFieldVariables = [...isFilledFieldVariables, ...[{id: index, value: true}]]
      }
      return rule;
    })
    if (isFilledFieldVariables.some((variable: Variable) => !variable.value)) {
      result.isFilledFieldObjectConditionRulesVariable = false;
    }
  }

  if (general_fields.calculation_type === lpsFormTypes.NON_INDUSTRIAL) {
    if (!lps_config_cities || !lps_config_cities.length) {
      result.isFilledFieldCity = false;
    }
  }
  if (
    general_fields.calculation_type === lpsFormTypes.KADASTER &&
    lps_config_entity_KOA_rules.lps_config_entity_KOA_points === ""
  ) {
    result.isFilledFieldConfigEntityKoaPoints = false;
  }

  const variablesArray = lpsc_object_condition_rules.map(
    (rule: any) => rule.variable
  );

  if (hasDuplicates(variablesArray)) {
    AlertService.warning(
      "You have duplicated fields in object condition rules"
    );
    result.isNotDuplicatedFields = false;
    return { result, isFilledFieldVariables };
  }
  const filteredEntries = Object.entries(result).filter(([key, _value]) => (key !== "isNotDuplicatedFieldName" && key !== "isFilledFieldObjectConditionRules" && key !== "isFilledFieldsObjectUnitsAmount" && key !== "isFilledFieldCalculationType"));
  const filteredResult = Object.fromEntries(filteredEntries);

  if (!Object.keys(filteredResult).every((item) => filteredResult[item])) {
    warning();
  }

  if (general_fields.calculation_type === lpsFormTypes.NON_INDUSTRIAL ) {
    if (
      !variablesArray.some((rule: any) => rule === "units amount") &&
      !variablesArray.some((rule: any) => rule === "")
    ) {
      AlertService.warning("Units amount is a required field");
      result.isFilledFieldObjectConditionRulesVariable = false;
    }
  }

  if (!result.isFilledFieldObjectConditionRules && result.isFilledFieldObjectConditionRulesVariable) {
    AlertService.warning("You have added an additional rule to the Object points condition block, fill in all the fields in it or delete it");
  }
  if (!result.isFilledFieldsObjectUnitsAmount && result.isFilledFieldObjectConditionRulesVariable) {
    AlertService.warning("Please fill in all the fields in the first line of the Object points condition block.");
  }

  return { result, isFilledFieldVariables };
};

const hasRealHouseNumber = (address: string): boolean => {
  const partsAddress = address.split(/\s+/);
  return partsAddress.some((part: string) => {
    return HOUSE_NUMBER_RE.test(part) && !INDEX_ADDRESS_RE.test(part);
  });
}

export const isValidAddress = (address: string): boolean => {
  const newAddress = (address ?? "").trim();
  if (!newAddress) return false;
  if (BAD_VALUE_RE.test(newAddress)) return false;
  if (BAD_CITY_RE.test(newAddress)) return false;
  return hasRealHouseNumber(newAddress);
}
